/*@flow*/
import type { Element } from 'react'; /** @jsx jsx */
import { css, jsx } from '@emotion/core';
import { useContext, useEffect, useState, createRef } from 'react';
import IntegrationContext from './IntegrationContext';
import GetGoogleFormButtons from '../queries/GetGoogleFormButtons';
import CreateGoogleFormButton from './mutations/CreateGoogleFormButton';
import UpdateGoogleFormButton from './mutations/UpdateGoogleFormButton';
import resource from 'lib/resource';
import underscored from 'underscore.string/underscored';
import { pascalize, depascalize } from 'humps';
import intCommands from './commands';
import * as t from 'lib/t';
import EmptyArray from 'lib/EmptyArray';
import NonEmptyArray from 'lib/NonEmptyArray';
import pick from 'ramda/src/pick';
import type { GoogleButton } from '../types';
import random from 'lib/random';
import buildUrlTemplate from './buildUrlTemplate';
import hash from 'lib/hash';
import { CreatableSelectTemplate, SelectTemplate } from 'lib/templates';
import LearnHow from './LearnHow';
import { useMutation } from '@apollo/react-hooks';
import compose from 'lib/compose';
import { Fields } from 'lib/proforma';
import * as I from 'immutable';

const refetchQueries = [
  {
    query: GetGoogleFormButtons,
  },
];

const getResourceNames = () =>
  Promise.resolve(
    Object.keys(resource)
      .map((v) => pascalize(v))
      .filter((v) => v !== 'Listing')
      .sort()
  );

const getResourceFields = (resourceName) => {
  const key = depascalize(resourceName);
  const fn = (k) =>
    compose(
      (v) => v.sort(),
      (v) => v.map((v2) => underscored(v2)),
      Object.keys,
      (v) => {
        try {
          return v[k].meta.props;
        } catch (e) {
          console.error(e);
          return {};
        }
      }
    );
  return Promise.resolve(fn(key)(resource));
};

const googleBtnFactory: I.RecordFactory<GoogleButton> = I.Record(
  {
    id: null,
    name: '',
    resourceName: '',
    showOnResourcePage: false,
    url: '',
    fields: [],
    urlTemplate: '',
  },
  'GoogleButton'
);

const mkId = random;

const mkGoogleBtn = (v) => googleBtnFactory({ id: mkId(), ...v });

const mkBtnPayload = (v) => {
  const btn = {
    name: v.name,
    url: v.url,
    fields: v.fields,
    resourceName: v.resourceName,
    urlTemplate: '',
    showOnResourcePage: v.showOnResourcePage,
  };
  btn.urlTemplate = buildUrlTemplate(btn);
  return btn;
};

export default (): Element<string> => {
  const intCtx = useContext(IntegrationContext);
  const {
    id,
    url,
    name,
    resourceName,
    fields,
    showOnResourcePage,
  } = intCtx.state;
  const opts = { refetchQueries };
  const [create] = useMutation(CreateGoogleFormButton, opts);
  const [save] = useMutation(UpdateGoogleFormButton, opts);

  const [availableResources, setResources] = useState([]);
  const [resourceFields, setResourceFields] = useState([]);
  const [errors, setErrors] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const formRef = createRef();

  useEffect(() => {
    if (resourceName) {
      // Get 'resourceFields' from the data storage.
      getResourceFields(resourceName).then((v) => {
        setResourceFields(v);
      });
    } else {
      // Clear 'resourceFields' and 'fields' when resourceName is blank.
      setResourceFields([]);
      intCommands(intCtx).set('fields', []);
    }
  }, [resourceName]);

  useEffect(() => {
    getResourceNames().then((v) => {
      setResources(v);
    });
  }, []);

  const NonEmptyString = t.mysubtype(t.String, (v) => !v && 'Cannot be blank.');
  const NonEmptyList = t.mysubtype(t.list(NonEmptyString), (v) =>
    EmptyArray.is(v) ? 'Cannot be blank.' : null
  );

  const schema = t.struct({
    name: NonEmptyString.options({
      label: 'Name (required)',
      help: <span>Label of the button on the form.</span>,
    }),
    showOnResourcePage: t.Boolean.options({
      label: 'Appears on resource pages',
    }),
    resourceName: NonEmptyString.options({
      label: 'Resource (required)',
      template: SelectTemplate,
      categories: availableResources,
      help: (
        <span>The name of resource form where the button will be shown.</span>
      ),
    }),
    fields: NonEmptyList.options({
      template: CreatableSelectTemplate,
      attrs: {
        isDisabled: resourceFields.length === 0,
      },
      isMulti: true,
      categories: resourceFields,
      help: (
        <span>
          Fields of the resource. Make sure to use these field names in your
          pre-filled Google Form. This will ensure they are populated when you
          click the button.
        </span>
      ),
    }),
    url: NonEmptyString.options({
      type: 'url',
      label: 'Url (required)',
      help: (
        <span>
          Hint: Get a pre-filled link from Google Forms. <LearnHow />
        </span>
      ),
    }),
  });

  const value = {
    url,
    name,
    resourceName,
    fields,
    showOnResourcePage,
  };

  const actionName = id ? 'Edit' : 'Create';

  const validate = () => {
    if (!formRef.current) return Promise.reject(['Form is not ready']);
    const validation = formRef.current.validate();
    if (!validation) {
      return Promise.reject(['Form is stale. Please reload the form.']);
    }
    const err = validation.errors;

    return EmptyArray.is(err) ? Promise.resolve() : Promise.reject(err);
  };

  const formKey = hash({
    resourceFields,
    availableResources,
  });

  return (
    <div
      data-testid="IntegrationForm"
      css={css(`
      .error-block.form-text {
        color: var(--danger);
      }
    `)}
    >
      <h1>{actionName} a button</h1>
      {NonEmptyArray.is(errors) && (
        <div className="alert alert-danger">
          <p>
            There are <b>{errors.length}</b> errors on the form. Please correct
            them and try again.
          </p>
        </div>
      )}
      <Fields
        type={schema}
        key={formKey}
        ref={formRef}
        value={value}
        onChange={(v) => {
          intCommands(intCtx).set('', (v2) =>
            v2
              .set('url', v.url)
              .set('name', v.name)
              .set('resourceName', v.resourceName)
              .set('showOnResourcePage', v.showOnResourcePage)
              .set('fields', v.fields)
          );
        }}
      />
      <a
        onClick={() => {
          if (isLoading) return;

          validate()
            .then(() => {
              setLoading(true);
              const picker = pick([
                'resourceName',
                'fields',
                'url',
                'name',
                'showOnResourcePage',
              ]);
              const v = picker(intCtx.state);
              const btnPayload = mkBtnPayload(v);
              const input = mkGoogleBtn(btnPayload).toJS();
              if (id) {
                input.id = id;
              }
              const action = id ? 'SAVE' : 'CREATE';
              let actionFn = id ? save : create;

              actionFn({ variables: input })
                .then((res) => {
                  if (action === 'SAVE') {
                    if (res.data.googleFormButtonUpdate.success) {
                      setTimeout(() => {
                        setLoading(false);
                        intCommands(intCtx).finish();
                      }, 800);
                    } else {
                      // TODO: handle errors
                    }
                  } else if (action === 'CREATE') {
                    if (res.data.googleFormButtonCreate.success) {
                      setTimeout(() => {
                        setLoading(false);
                        intCommands(intCtx).finish();
                      }, 800);
                    } else {
                      // TODO: handle errors
                    }
                  }
                })
                .catch((err) => {
                  console.error(err);
                  setLoading(false);
                });
            })
            .catch((err) => {
              setErrors(err);
            });
        }}
        css={css(`
          &:hover {
            color: var(--info) !important;
          }
        `)}
        className="btn btn-outline-info border border-info btn-lg bg-white mt-4"
        tabIndex={0}
        disabled={isLoading}
        data-testid="IntegrationSaveBtn"
      >
        {isLoading ? 'Saving...' : 'Save'}
      </a>
    </div>
  );
};
